import { PresetView, PresetViewScope, PresetViewSupportedView } from '@bringg/types';
import { getRoot } from 'mobx-easy';
import { BringgFontIcons } from '@bringg/bringg-icons';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';

import RootStore from 'bringg-web/stores/root-store';
import { executeAction } from '../../../services/cross-application/cross-application';
import { PresetViews } from '../../../services/cross-application/cross-application.actions';
import { presetViewsRoot } from '../stores/data-providers/preset-views-root';
import type { ClientSideConfig } from '../stores/stores/types';

export const PRESETS_VIEWS_FEATURE_FLAG = 'enable_preset_views';

export const TASK_ALERTS_TABLE_KEY = 'taskAlerts';
export const DRIVER_ALERTS_TABLE_KEY = 'driverAlerts';
export const OPTIMIZATION_ALERTS_KEY = 'optimizationAlerts';

export const getPresetIcon = (scope: PresetViewScope) =>
	scope === PresetViewScope.Merchant ? BringgFontIcons.Monitor : BringgFontIcons.User;

export const getPresetViewType = (viewKey: string) => {
	if (
		viewKey.includes('list') || // list - is table config key
		viewKey.includes('map') || // map - disaptch map screen
		viewKey.includes('DispatchView') // DispatchView - is screen filters key
	) {
		return PresetViewSupportedView.Dispatch;
	}

	if (
		viewKey.includes('planning') || // planning - is table config key
		viewKey.includes('PlanningView') // PlanningView - is screen filters key
	) {
		return PresetViewSupportedView.Planning;
	}

	if (viewKey === TASK_ALERTS_TABLE_KEY) {
		return PresetViewSupportedView.OrderAlerts;
	}

	if (viewKey === DRIVER_ALERTS_TABLE_KEY) {
		return PresetViewSupportedView.DriverAlerts;
	}

	if (viewKey === OPTIMIZATION_ALERTS_KEY) {
		return PresetViewSupportedView.OptimisationAlerts;
	}

	return null;
};

export const loadAllPresetsInfo = async () => {
	const presetViewStoreRepo = getRootEnv().dashboardSdk.sdk.v2.presetViews();
	const userPresetViewsStoreRepo = getRootEnv().dashboardSdk.sdk.v2.userPresetViews;

	return await Promise.all([presetViewStoreRepo.loadAll(), userPresetViewsStoreRepo.loadAll()]);
};

export const sendPresetViewsChangedEvent = (event?: { view: PresetViewSupportedView; preset_view_id: number }) => {
	executeAction(getRoot<RootStore>().views.authStore.crossAppTransport, PresetViews.PRESET_VIEWS_UPDATED, event);
};

export const sendPresetViewsInitiatedEvent = () => {
	executeAction(getRoot<RootStore>().views.authStore.crossAppTransport, PresetViews.PRESET_VIEWS_INITIATED);
};

export const getActivePresetViewForView = (viewType: PresetViewSupportedView) => {
	let activePresetView: ClientSideConfig;

	const { presetViewStoreRepo, userPresetViewsStoreRepo } = presetViewsRoot.getStore();
	const [userPresetView] = userPresetViewsStoreRepo.getPresetViewsByViewName(viewType);

	if (userPresetView) {
		activePresetView = presetViewStoreRepo.get(userPresetView?.preset_view_id);
	}

	return activePresetView || getMerchantDefaultPreset(viewType);
};

const getMerchantDefaultPreset = viewType => {
	const { presetViewStoreRepo } = presetViewsRoot.getStore();

	return presetViewStoreRepo
		.getAll()
		.find(item => item.view === viewType && item.isDefault && item.scope === PresetViewScope.Merchant);
};

type PickPresetScope = Pick<PresetView, 'scope'>;
export const sortByPresetScope = (a: PickPresetScope, b: PickPresetScope) => {
	const [scopeOneNumeric, scopeTwoNumeric] = [
		a.scope === PresetViewScope.Merchant ? 1 : 0,
		b.scope === PresetViewScope.Merchant ? 1 : 0
	];
	return scopeOneNumeric === scopeTwoNumeric ? 0 : scopeTwoNumeric - scopeOneNumeric;
};
